import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import { ValueType } from "react-select";
import LabeledInput, { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import customXGSSelectStyles from "ui-components/xgs-select/customXGSSelectStyles";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { DockTag } from "app/data/dock-scan/models";

export interface DockEmployeeTagSelectProps {
  tags: DockTag[];
  value: XGSSelectOption[];
  onChange: (options: XGSSelectOption[]) => void;
  disabled?: boolean;
  label?: string;
}

const NEW_TAG_PREFIX = "__new__:";

export function isNewTagOption(option: XGSSelectOption): boolean {
  return (
    !!(option as XGSSelectOption & { __isNew__?: boolean }).__isNew__ ||
    String(option.value).startsWith(NEW_TAG_PREFIX)
  );
}

export function toTagSelectOptions(tags: DockTag[]): XGSSelectOption[] {
  return tags.map((t) => ({ value: t.id, label: t.name }));
}

/** Resolve selected options to tag IDs, creating any new tags first. */
export async function resolveTagIdsFromSelections(
  selections: XGSSelectOption[],
  existingTags: DockTag[],
  createTag: (name: string) => Promise<DockTag>
): Promise<string[]> {
  const ids: string[] = [];
  const knownById = new Map(existingTags.map((t) => [t.id, t]));
  const knownByName = new Map(existingTags.map((t) => [t.name.trim().toLowerCase(), t]));

  for (const sel of selections) {
    const nameKey = sel.label.trim().toLowerCase();
    const byId = knownById.get(String(sel.value));
    const byName = knownByName.get(nameKey);

    if (byId) {
      ids.push(byId.id);
      continue;
    }
    if (byName) {
      ids.push(byName.id);
      continue;
    }

    if (isNewTagOption(sel)) {
      const created = await createTag(sel.label.trim());
      knownById.set(created.id, created);
      knownByName.set(created.name.trim().toLowerCase(), created);
      ids.push(created.id);
      continue;
    }

    ids.push(String(sel.value));
  }

  return ids;
}

const multiSelectStyles = {
  ...customXGSSelectStyles,
  control: (provided: React.CSSProperties, state: unknown) => ({
    ...(customXGSSelectStyles.control?.(provided, state as never) || provided),
    height: "auto",
    minHeight: 30,
  }),
  indicatorsContainer: (provided: React.CSSProperties, state: unknown) => ({
    ...(customXGSSelectStyles.indicatorsContainer?.(provided, state as never) || provided),
    height: "auto",
    minHeight: 30,
    alignSelf: "stretch",
  }),
};

const DockEmployeeTagSelect: React.FC<DockEmployeeTagSelectProps> = ({
  tags,
  value,
  onChange,
  disabled,
  label = "Tags",
}) => {
  const options = useMemo(() => toTagSelectOptions(tags), [tags]);

  return (
    <LabeledInput label={label} labelMode={LabelModes.column} isFailed={() => false}>
      <CreatableSelect
        classNamePrefix="xgs-select"
        styles={multiSelectStyles}
        isMulti
        isClearable
        isDisabled={disabled}
        options={options}
        value={value}
        placeholder="Add tag..."
        noOptionsMessage={() => "Type to create a tag"}
        formatCreateLabel={(input) => `Create "${input.trim()}"`}
        components={{ IndicatorSeparator: () => null }}
        onChange={(selected: ValueType<XGSSelectOption>) => {
          if (!selected) {
            onChange([]);
            return;
          }
          const next = (Array.isArray(selected) ? selected : [selected]) as XGSSelectOption[];
          onChange(
            next.map((opt) => {
              if (isNewTagOption(opt)) {
                return {
                  ...opt,
                  value: `${NEW_TAG_PREFIX}${opt.label.trim()}`,
                  label: opt.label.trim(),
                };
              }
              return opt;
            })
          );
        }}
        isValidNewOption={(inputValue, _selectValue, selectOptions) => {
          const trimmed = inputValue.trim();
          if (!trimmed) return false;
          const lower = trimmed.toLowerCase();
          return !(selectOptions as XGSSelectOption[]).some(
            (o) => o.label.trim().toLowerCase() === lower
          );
        }}
      />
      <div className="dock-users__tag-hint">Press Enter to add a tag</div>
    </LabeledInput>
  );
};

export default DockEmployeeTagSelect;
